import React, { useState, useEffect } from "react";
import {
  useSearchParams
} from "react-router-dom";
import {
  Loader2,
  AlertTriangle,
  Search
} from "lucide-react";
import {
  getSectionAttendance
} from "../../api/studentattendance/studentAttendancePortalApi";
import {
  useStaffAuth
} from "../../context/StaffAuthContext";
import {
  Card,
  CardContent,
  Badge,
  Button,
  Skeleton
} from "../../components/ui";
import {
  useToast
} from "../../context/ToastContext";

const STATUS_BADGE_VARIANTS = {
  PRESENT: "success",
  ABSENT: "danger",
  LATE: "warning",
  HOLIDAY: "primary",
  LEAVE: "purple",
  NOT_MARKED: "secondary",
};

const getTodayString = () => {
  const today = new Date();
  return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
};

const SectionAttendance = () => {
  const [searchParams] = useSearchParams();
  const { classTeacherSections } = useStaffAuth();

  const initialSection = searchParams.get("sectionId") || "";

  const [sectionId, setSectionId] = useState(initialSection);
  const toast = useToast();

  const [viewMode, setViewMode] = useState("date"); // "date" | "month"
  const [date, setDate] = useState(getTodayString());
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const sectionOptions = (classTeacherSections || []).map((s) => ({
    id: s._id,
    label: `${s.classId?.className || "Class"} - ${s.sectionName}`,
  }));

  const load = async () => {
    if (!sectionId) {
      toast.error("Please select or enter a section");
      return;
    }
    setLoading(true);
    try {
      let filters = {};
      if (viewMode === "date") {
        filters = { date };
      } else {
        filters = { year, month };
      }
      const res = await getSectionAttendance(sectionId, filters);
      setData(res);
      setLoaded(true);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Failed to load attendance");
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (initialSection) {
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const records = data?.records || [];

  // Summary for date view
  let present = 0,
    absent = 0,
    late = 0;
  records.forEach((r) => {
    if (r.status === "PRESENT") present += 1;
    else if (r.status === "ABSENT") absent += 1;
    else if (r.status === "LATE") late += 1;
  });

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto space-y-6">
      <div className="pb-4 border-b border-neutral-200 dark:border-neutral-800/80">
        <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100 tracking-tight">
          Section Attendance
        </h1>
        <p className="text-xs sm:text-sm font-semibold text-neutral-500 dark:text-neutral-400 mt-1">
          Review daily and monthly section-wide student attendance
        </p>
      </div>

      {/* Filters Bar */}
      <Card className="border border-neutral-200 dark:border-neutral-800/80 bg-white dark:bg-neutral-900 rounded-2xl p-4 shadow-xs">
        <div className="flex flex-wrap items-end gap-3">
          {sectionOptions.length > 0 ? (
            <div className="flex-1 min-w-[180px]">
              <label className="block text-[11px] font-bold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-1">
                Section
              </label>
              <select
                value={sectionId}
                onChange={(e) => setSectionId(e.target.value)}
                className="w-full border border-neutral-200 dark:border-neutral-800 rounded-xl px-3 py-2 text-xs font-bold text-neutral-800 dark:text-neutral-100 bg-neutral-50/50 dark:bg-neutral-950 shadow-2xs focus:bg-white outline-none"
              >
                <option value="">Select section</option>
                {sectionOptions.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <div className="flex-1 min-w-[200px]">
              <label className="block text-[11px] font-bold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-1">
                Section ID
              </label>
              <input
                type="text"
                value={sectionId}
                onChange={(e) => setSectionId(e.target.value)}
                placeholder="Paste section ObjectId"
                className="w-full border border-neutral-200 dark:border-neutral-800 rounded-xl px-3 py-2 text-xs font-bold text-neutral-800 dark:text-neutral-100 bg-neutral-50/50 dark:bg-neutral-950 shadow-2xs focus:bg-white outline-none"
              />
            </div>
          )}

          <div>
            <label className="block text-[11px] font-bold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-1">
              View
            </label>
            <div className="flex rounded-xl bg-slate-100 dark:bg-neutral-900 p-1">
              <Button
                onClick={() => setViewMode("date")}
                variant={viewMode === "date" ? "primary" : "ghost"}
                size="xs"
                className={`rounded-lg px-3 py-1 text-xs font-bold transition-all ${
                  viewMode === "date" ? "bg-indigo-600 text-white shadow-2xs" : "text-neutral-600 dark:text-neutral-300"
                }`}
              >
                By Date
              </Button>
              <Button
                onClick={() => setViewMode("month")}
                variant={viewMode === "month" ? "primary" : "ghost"}
                size="xs"
                className={`rounded-lg px-3 py-1 text-xs font-bold transition-all ${
                  viewMode === "month" ? "bg-indigo-600 text-white shadow-2xs" : "text-neutral-600 dark:text-neutral-300"
                }`}
              >
                By Month
              </Button>
            </div>
          </div>

          {viewMode === "date" ? (
            <div>
              <label className="block text-[11px] font-bold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-1">
                Date
              </label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="border border-neutral-200 dark:border-neutral-800 rounded-xl px-3 py-2 text-xs font-bold text-neutral-800 dark:text-neutral-100 bg-neutral-50/50 dark:bg-neutral-950 shadow-2xs outline-none"
              />
            </div>
          ) : (
            <>
              <div>
                <label className="block text-[11px] font-bold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-1">
                  Year
                </label>
                <input
                  type="number"
                  value={year}
                  onChange={(e) => setYear(Number(e.target.value))}
                  className="border border-neutral-200 dark:border-neutral-800 rounded-xl px-3 py-2 text-xs font-bold text-neutral-800 dark:text-neutral-100 bg-neutral-50/50 dark:bg-neutral-950 shadow-2xs w-20 outline-none"
                />
              </div>
              <div>
                <label className="block text-[11px] font-bold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-1">
                  Month
                </label>
                <select
                  value={month}
                  onChange={(e) => setMonth(Number(e.target.value))}
                  className="border border-neutral-200 dark:border-neutral-800 rounded-xl px-3 py-2 text-xs font-bold text-neutral-800 dark:text-neutral-100 bg-neutral-50/50 dark:bg-neutral-950 shadow-2xs outline-none"
                >
                  {Array.from({ length: 12 }, (_, i) => (
                    <option key={i + 1} value={i + 1}>
                      {new Date(2000, i).toLocaleString("default", {
                        month: "long",
                      })}
                    </option>
                  ))}
                </select>
              </div>
            </>
          )}

          <Button
            onClick={load}
            disabled={loading}
            variant="primary"
            size="sm"
            className="rounded-xl px-5 py-2 font-bold bg-indigo-600 hover:bg-indigo-700 text-white shadow-xs active:scale-95 transition-all"
          >
            {loading ? (
              <Loader2 className="animate-spin" size={16} />
            ) : (
              <>
                <Search size={16} className="mr-1.5" />
                Load
              </>
            )}
          </Button>
        </div>
      </Card>

      

      {!loaded && !loading && (
        <Card className="border border-neutral-200 dark:border-neutral-800/80 bg-white dark:bg-neutral-900 rounded-2xl p-12 text-center shadow-xs">
          <p className="text-xs font-bold text-neutral-500 dark:text-neutral-400">
            Select a class section and load options to view attendance records.
          </p>
        </Card>
      )}

      {loading && (
        <div className="space-y-4">
          <Skeleton height={40} width="100%" className="rounded-2xl" />
          <Skeleton height={200} width="100%" className="rounded-2xl" />
        </div>
      )}

      {loaded && !loading && viewMode === "date" && (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2.5">
            <Badge variant="success" className="px-3 py-1 text-xs font-extrabold rounded-full">
              Present: {present}
            </Badge>
            <Badge variant="danger" className="px-3 py-1 text-xs font-extrabold rounded-full">
              Absent: {absent}
            </Badge>
            <Badge variant="warning" className="px-3 py-1 text-xs font-extrabold rounded-full">
              Late: {late}
            </Badge>
          </div>

          <Card className="border border-neutral-200 dark:border-neutral-800/80 bg-white dark:bg-neutral-900 rounded-2xl overflow-hidden shadow-xs">
            <div className="overflow-x-auto">
              <table className="w-full text-xs text-left">
                <thead className="bg-slate-50/80 text-neutral-600 dark:text-neutral-300 font-extrabold border-b border-neutral-200 dark:border-neutral-800">
                  <tr>
                    <th className="px-4 py-3">Student</th>
                    <th className="px-4 py-3">Admission</th>
                    <th className="px-4 py-3">Roll</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Remark</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-neutral-800">
                  {records.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-4 py-8 text-center text-neutral-500 dark:text-neutral-400 font-semibold">
                        No records found for this date
                      </td>
                    </tr>
                  ) : (
                    records.map((r, i) => {
                      const st = r.student || {};
                      const name =
                        st.name ||
                        `${st.firstName || ""} ${st.lastName || ""}`.trim() ||
                        "—";
                      return (
                        <tr key={r._id || i} className="hover:bg-neutral-50 dark:hover:bg-neutral-800/60 transition-colors">
                          <td className="px-4 py-3 font-extrabold text-neutral-800 dark:text-neutral-100">{name}</td>
                          <td className="px-4 py-3 text-neutral-500 dark:text-neutral-400 font-semibold">
                            {st.admissionNo || "—"}
                          </td>
                          <td className="px-4 py-3 text-neutral-500 dark:text-neutral-400 font-semibold">
                            {st.rollNumber || "—"}
                          </td>
                          <td className="px-4 py-3">
                            <Badge
                              variant={STATUS_BADGE_VARIANTS[r.status] || "secondary"}
                              className="px-2.5 py-0.5 text-[10px] font-extrabold rounded-full uppercase"
                            >
                              {r.status}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-neutral-500 dark:text-neutral-400 font-medium">
                            {r.remark || "—"}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}

      {loaded && !loading && viewMode === "month" && (
        <Card className="border border-neutral-200 dark:border-neutral-800/80 bg-white dark:bg-neutral-900 rounded-2xl p-6 shadow-xs">
          <CardContent className="p-0 space-y-4">
            <div className="text-center text-neutral-600 dark:text-neutral-300">
              <p className="text-xs font-extrabold">Monthly Matrix Summary</p>
              <p className="text-xs font-semibold text-neutral-400 dark:text-neutral-500 mt-0.5">
                Loaded {records.length} total attendance logs for the month.
              </p>
            </div>

            <div className="overflow-x-auto rounded-xl border border-neutral-200 dark:border-neutral-800/80">
              <table className="w-full text-xs text-left">
                <thead className="bg-neutral-50 dark:bg-neutral-950 text-neutral-600 dark:text-neutral-300 font-extrabold border-b border-neutral-200 dark:border-neutral-800">
                  <tr>
                    <th className="px-3 py-2.5">Date</th>
                    <th className="px-3 py-2.5">Student</th>
                    <th className="px-3 py-2.5">Status</th>
                    <th className="px-3 py-2.5">Remark</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-neutral-800">
                  {records.map((r, i) => {
                    const st = r.student || {};
                    const name =
                      st.name ||
                      `${st.firstName || ""} ${st.lastName || ""}`.trim() ||
                      "—";
                    return (
                      <tr key={r._id || i} className="hover:bg-neutral-50 dark:hover:bg-neutral-800/60">
                        <td className="px-3 py-2 font-bold text-neutral-700 dark:text-neutral-200">{r.date}</td>
                        <td className="px-3 py-2 font-extrabold text-neutral-800 dark:text-neutral-100">{name}</td>
                        <td className="px-3 py-2">
                          <Badge
                            variant={STATUS_BADGE_VARIANTS[r.status] || "secondary"}
                            className="px-2 py-0.5 text-[10px] font-extrabold uppercase rounded-full"
                          >
                            {r.status}
                          </Badge>
                        </td>
                        <td className="px-3 py-2 text-neutral-500 dark:text-neutral-400 font-medium">{r.remark || "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SectionAttendance;